import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPolicyImpact, fetchPolicyCorporate, fetchPolicyTracks, fetchPolicyTimeline } from "../../api";
import type { PolicyTrackMeta, PolicyTrack, PolicyArticle } from "../../api";
import { Link } from "react-router-dom";
import { formatDate } from "../../utils/date";
import { FilterBtn } from "../../components/FilterBtn";
import { SOURCE_COMPANY } from "../../constants/companies";

/* ── Policy type filters (interest_rate_cap removed per user request) ── */
const POLICY_TYPES = [
  { value: "", label: "全部类型" },
  { value: "lending_regulation", label: "助贷监管" },
  { value: "license", label: "牌照" },
  { value: "data_compliance", label: "数据合规" },
  { value: "consumer_protection", label: "消费者保护" },
  { value: "capital_requirement", label: "资本" },
  { value: "anti_money_laundering", label: "反洗钱" },
  { value: "other", label: "其他" },
];

const IMPACT_LEVELS = [
  { value: "", label: "全部影响" },
  { value: "critical", label: "重大" },
  { value: "high", label: "高" },
  { value: "medium", label: "中" },
  { value: "low", label: "低" },
];

const RANGE_OPTIONS = [
  { value: 90, label: "近90天" },
  { value: 180, label: "近半年" },
  { value: 365, label: "近一年" },
  { value: 730, label: "近两年" },
];

const IMPACT_DOT: Record<string, string> = {
  critical: "bg-rose-500",
  high: "bg-amber-500",
  medium: "bg-orange-400",
  low: "bg-emerald-500",
};

const IMPACT_TEXT: Record<string, string> = {
  critical: "text-rose-600 dark:text-rose-400",
  high: "text-amber-700 dark:text-amber-400",
  medium: "text-orange-600 dark:text-orange-400",
  low: "text-emerald-700 dark:text-emerald-400",
};

const IMPACT_LABELS: Record<string, string> = {
  critical: "重大",
  high: "高",
  medium: "中",
  low: "低",
};

const TYPE_LABELS: Record<string, string> = {
  lending_regulation: "助贷监管",
  license: "牌照",
  data_compliance: "数据合规",
  capital_requirement: "资本",
  consumer_protection: "消费者保护",
  anti_money_laundering: "反洗钱",
  other: "其他",
};

interface CorporateArticle extends PolicyArticle {
  // Extends shared PolicyArticle with additional fields
}

function ImportanceBadge({ score }: { score?: number }) {
  if (!score || score < 3) return null;

  const style =
    score >= 5
      ? { label: "紧要", cls: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300" }
      : score >= 4
        ? { label: "重要", cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300" }
        : { label: "关注", cls: "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-900/20 dark:text-orange-300" };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-semibold ${style.cls}`}>
      <span className="h-1 w-1 rounded-full bg-current opacity-70" />
      {style.label}
    </span>
  );
}

/* ── Track item: collapsible with inline expand ── */

/* Track color palette — muted, desaturated tints for a Bloomberg/FT feel.
   Light-mode gradients use /6~/2 opacity; dots & lines use -300/-400 shades;
   badge text uses -500/-600 (never -700, too heavy). */
const TRACK_THEME: Record<string, {
  gradient: string;
  dot: string;
  line: string;
  impactDot: string;
  badge: string;
}> = {
  data_compliance: {
    gradient: "from-slate-400/6 to-slate-500/2 dark:from-slate-400/10 dark:to-slate-500/5",
    dot: "bg-slate-400",
    line: "bg-slate-200 dark:bg-slate-700",
    impactDot: "bg-slate-500",
    badge: "text-slate-500 dark:text-slate-400",
  },
  consumer_protection: {
    gradient: "from-zinc-400/6 to-zinc-500/2 dark:from-zinc-400/10 dark:to-zinc-500/5",
    dot: "bg-zinc-400",
    line: "bg-zinc-200 dark:bg-zinc-700",
    impactDot: "bg-zinc-500",
    badge: "text-zinc-500 dark:text-zinc-400",
  },
  license_entry: {
    gradient: "from-amber-300/6 to-amber-400/2 dark:from-amber-400/10 dark:to-amber-500/5",
    dot: "bg-amber-400",
    line: "bg-amber-200 dark:bg-amber-700",
    impactDot: "bg-amber-400",
    badge: "text-amber-600 dark:text-amber-400",
  },
  internet_lending: {
    gradient: "from-zinc-400/6 to-zinc-500/2 dark:from-zinc-400/10 dark:to-zinc-500/5",
    dot: "bg-zinc-400",
    line: "bg-zinc-200 dark:bg-zinc-700",
    impactDot: "bg-zinc-400",
    badge: "text-zinc-500 dark:text-zinc-400",
  },
  capital_risk: {
    gradient: "from-rose-300/6 to-rose-400/2 dark:from-rose-300/10 dark:to-rose-400/5",
    dot: "bg-rose-300",
    line: "bg-rose-200 dark:bg-rose-700",
    impactDot: "bg-rose-400",
    badge: "text-rose-500 dark:text-rose-400",
  },
  business_loan: {
    gradient: "from-stone-400/6 to-stone-500/2 dark:from-stone-400/10 dark:to-stone-500/5",
    dot: "bg-stone-400",
    line: "bg-stone-200 dark:bg-stone-600",
    impactDot: "bg-stone-500",
    badge: "text-stone-500 dark:text-stone-400",
  },
};

const DEFAULT_THEME = {
  gradient: "from-zinc-500/8 to-zinc-600/3 dark:from-zinc-500/15 dark:to-zinc-600/8",
  dot: "bg-ink-tertiary",
  line: "bg-border",
  impactDot: "bg-ink-tertiary",
  badge: "text-ink-tertiary",
};

function TrackItem({
  track,
  isExpanded,
  onToggle,
  timelineData,
  timelineLoading,
}: {
  track: PolicyTrackMeta;
  isExpanded: boolean;
  onToggle: () => void;
  timelineData: PolicyTrack | null;
  timelineLoading: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const theme = TRACK_THEME[track.track_id] ?? DEFAULT_THEME;

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [timelineData, timelineLoading]);

  const latest = track.latest_milestone;

  return (
    <div className="group">
      {/* Collapsed header — always visible */}
      <button
        onClick={onToggle}
        className={`w-full text-left transition-colors duration-100 ${
          isExpanded ? "bg-surface-sunken/50" : "hover:bg-surface"
        }`}
      >
        <div className={`flex items-start gap-4 px-5 py-4 sm:px-6 bg-gradient-to-r ${theme.gradient}`}>
          <div className="min-w-0 flex-1 space-y-2">
            {/* Track name + color dot + date range */}
            <div className="flex items-center gap-2.5">
              <span className={`h-2 w-2 rounded-full shrink-0 ${theme.dot}`} />
              <h3 className="text-[15px] font-semibold tracking-tight text-ink">
                {track.track_name}
              </h3>
              {track.date_range && (
                <span className="text-2xs font-mono tabular-nums text-ink-faint">
                  {track.date_range}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-sm leading-6 text-ink-secondary line-clamp-1 pl-[18px]">
              {track.track_desc}
            </p>

            {/* Latest milestone preview */}
            {latest && (
              <div className="flex items-center gap-2 text-2xs pl-[18px]">
                <span className="text-ink-faint">最新</span>
                <span className="font-mono tabular-nums text-ink-tertiary">{latest.date}</span>
                <span className="text-ink-secondary font-medium truncate max-w-[360px]">
                  {latest.title}
                </span>
                {latest.is_draft && (
                  <span className="rounded border border-border px-1.5 py-px text-ink-faint">
                    征求意见
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right side: stats + chevron */}
          <div className="flex items-center gap-3 shrink-0 pt-1">
            <div className="flex items-center gap-2 text-2xs text-ink-tertiary">
              <span className="font-mono tabular-nums">
                {track.milestone_count} 节点
              </span>
              <span className="text-ink-faint">·</span>
              <span className={`font-mono tabular-nums font-medium ${theme.badge}`}>
                {track.critical_count} 关键
              </span>
            </div>
            <svg
              className={`h-4 w-4 text-ink-faint transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        </div>
      </button>

      {/* Expanded milestones — inline under this track */}
      <div
        className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: isExpanded ? `${contentHeight + 32}px` : "0px" }}
      >
        <div ref={contentRef}>
          {timelineLoading && (
            <div className="border-t border-border">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-2 px-5 py-4 sm:px-6 animate-pulse border-b border-border last:border-b-0">
                  <div className="h-2.5 w-28 rounded bg-surface-sunken" />
                  <div className="h-3.5 w-3/4 rounded bg-surface-sunken" />
                  <div className="h-3 w-2/3 rounded bg-surface-sunken" />
                </div>
              ))}
            </div>
          )}

          {!timelineLoading && timelineData && (
            <div className="border-t border-border">
              {/* Vertical timeline */}
              {timelineData.milestones.map((m, idx) => {
                const isLast = idx === timelineData.milestones.length - 1;
                const isCritical = m.impact_level === "critical";
                const isHigh = m.impact_level === "high";

                return (
                  <div
                    key={m.seq}
                    className={`relative pl-10 pr-5 py-4 sm:pl-12 sm:pr-6 ${
                      !isLast ? "border-b border-border" : ""
                    }`}
                  >
                    {/* Timeline line — track-colored */}
                    <div className={`absolute left-[26px] sm:left-[34px] top-0 bottom-0 w-px ${theme.line}`} />

                    {/* Timeline dot — track-colored for critical/high, muted for others */}
                    <div
                      className={`absolute left-[22px] sm:left-[30px] top-[22px] rounded-full border-2 border-panel ${
                        isCritical
                          ? `h-[11px] w-[11px] ${theme.impactDot}`
                          : isHigh
                            ? `h-[9px] w-[9px] ${theme.impactDot} opacity-60`
                            : "h-[9px] w-[9px] bg-border-strong"
                      }`}
                    />

                    {/* Meta line */}
                    <div className="flex flex-wrap items-center gap-2 text-2xs text-ink-tertiary mb-1.5">
                      <span className={`font-mono tabular-nums ${isCritical || isHigh ? "font-semibold text-ink-secondary" : ""}`}>
                        {m.date}
                      </span>
                      <span className={`${
                        isCritical
                          ? `font-semibold ${theme.badge}`
                          : isHigh
                            ? "text-ink-tertiary font-medium"
                            : "text-ink-faint"
                      }`}>
                        {IMPACT_LABELS[m.impact_level] ?? m.impact_level}
                      </span>
                      {m.is_draft && (
                        <span className="rounded border border-border px-1.5 py-px text-ink-faint">
                          征求意见稿
                        </span>
                      )}
                      <span className="text-ink-faint">{m.issuer}</span>
                    </div>

                    {/* Title */}
                    <p className={`text-[14px] leading-6 ${
                      isCritical
                        ? "font-semibold text-ink"
                        : "font-medium text-ink"
                    }`}>
                      {m.title}
                    </p>
                    {m.doc_number && m.doc_number !== "\u2014" && (
                      <p className="text-2xs font-mono text-ink-faint mt-0.5">{m.doc_number}</p>
                    )}

                    {/* Core rule */}
                    <p className="mt-1.5 text-sm leading-6 text-ink-secondary">{m.core_rule}</p>

                    {/* Industry impact — subtle bg */}
                    <div className="mt-2 rounded border border-border bg-surface px-3 py-2">
                      <p className="text-2xs font-medium text-ink-tertiary mb-0.5">行业影响</p>
                      <p className="text-sm leading-6 text-ink-secondary">{m.industry_impact}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!timelineLoading && !timelineData && (
            <div className="border-t border-border px-6 py-10 text-center">
              <p className="text-sm text-ink-secondary">暂无该主线的数据</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function PolicyPage() {
  const [policyType, setPolicyType] = useState("");
  const [impactLevel, setImpactLevel] = useState("");
  const [days, setDays] = useState(365);
  const [expandedTrack, setExpandedTrack] = useState<string | null>(null);

  const { data: tracksMeta = [] } = useQuery<PolicyTrackMeta[]>({
    queryKey: ["policy-tracks"],
    queryFn: fetchPolicyTracks,
  });

  const { data: timelineData = [], isLoading: timelineLoading } = useQuery<PolicyTrack[]>({
    queryKey: ["policy-timeline", expandedTrack],
    queryFn: () => fetchPolicyTimeline(expandedTrack ?? undefined),
    enabled: expandedTrack !== null,
  });

  const { data: articles = [], isLoading } = useQuery<PolicyArticle[]>({
    queryKey: ["policy-impact", { policyType, impactLevel, days }],
    queryFn: () =>
      fetchPolicyImpact({
        policy_type: policyType || undefined,
        impact_level: impactLevel || undefined,
        days,
        official_only: true,
      }),
  });

  const { data: corpArticles = [] } = useQuery<CorporateArticle[]>({
    queryKey: ["policy-corporate", days],
    queryFn: () => fetchPolicyCorporate({ days: Math.min(days, 90) }),
  });

  const handleToggle = useCallback((trackId: string) => {
    setExpandedTrack((prev) => (prev === trackId ? null : trackId));
  }, []);

  const expandedTrackData = expandedTrack
    ? timelineData.find((t) => t.track_id === expandedTrack) ?? null
    : null;

  return (
    <div className="space-y-5">
      {/* ── Page header (matches Home/Industry pattern) ── */}
      <section className="paper-panel-elevated overflow-hidden">
        <div className="border-b border-border px-5 py-4 sm:px-6">
          <p className="section-kicker">Policy Watch</p>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl space-y-2">
              <h1 className="text-[22px] font-semibold tracking-tight text-ink sm:text-[24px]">
                政策与企业合规动态
              </h1>
              <p className="text-sm leading-6 text-ink-secondary">
                六条监管主线梳理助贷行业的制度演进脉络，下方为实时政策 feed 与企业合规动态。
              </p>
            </div>
            <div className="flex items-center gap-2 text-2xs text-ink-tertiary shrink-0">
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                {tracksMeta.length} 条主线
              </span>
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                {tracksMeta.reduce((sum, t) => sum + t.milestone_count, 0)} 个节点
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 1: 监管主线 — collapsible tracks with inline timeline ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <div>
            <p className="text-label">Regulatory Tracks</p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-ink">监管主线</h2>
          </div>
        </div>

        <div className="paper-panel overflow-hidden divide-y divide-border">
          {tracksMeta.map((track) => (
            <TrackItem
              key={track.track_id}
              track={track}
              isExpanded={expandedTrack === track.track_id}
              onToggle={() => handleToggle(track.track_id)}
              timelineData={expandedTrack === track.track_id ? expandedTrackData : null}
              timelineLoading={expandedTrack === track.track_id && timelineLoading}
            />
          ))}
        </div>
      </section>

      {/* ── Section 2: 筛选 + 政策 Feed ── */}
      <section className="paper-panel overflow-hidden">
        <div className="space-y-4 px-5 py-5 sm:px-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-label">政策类型</p>
              <div className="flex items-center gap-3 text-2xs text-ink-faint">
                <span>官方来源优先</span>
                <span className="rounded-full border border-border bg-surface px-2.5 py-0.5 font-mono tabular-nums">
                  {articles.length} 条
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {POLICY_TYPES.map((pt) => (
                <FilterBtn key={pt.value} active={policyType === pt.value} onClick={() => setPolicyType(pt.value)}>
                  {pt.label}
                </FilterBtn>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2">
              <p className="text-label">影响等级</p>
              <div className="flex flex-wrap gap-1.5">
                {IMPACT_LEVELS.map((il) => (
                  <FilterBtn key={il.value} active={impactLevel === il.value} onClick={() => setImpactLevel(il.value)}>
                    {il.label}
                  </FilterBtn>
                ))}
              </div>
            </div>

            <div className="space-y-2 lg:items-end">
              <p className="text-label">时间范围</p>
              <div className="flex flex-wrap gap-1.5">
                {RANGE_OPTIONS.map((option) => (
                  <FilterBtn key={option.value} active={days === option.value} onClick={() => setDays(option.value)}>
                    {option.label}
                  </FilterBtn>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Corporate compliance ── */}
      {corpArticles.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div>
              <p className="text-label">Corporate Compliance</p>
              <h2 className="mt-1 text-lg font-semibold tracking-tight text-ink">企业合规动态</h2>
            </div>
            <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-mono tabular-nums text-ink-tertiary">
              {corpArticles.length} 条
            </span>
          </div>

          <div className="paper-panel overflow-hidden divide-y divide-border">
            {corpArticles.slice(0, 8).map((article) => (
              <div key={article.id} className="group px-5 py-4 transition-colors hover:bg-surface">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-2xs text-ink-tertiary">
                  {SOURCE_COMPANY[article.source_code ?? ""] && (
                    <span className="font-medium text-accent-dark dark:text-accent">
                      {SOURCE_COMPANY[article.source_code ?? ""]}
                    </span>
                  )}
                  <span>{article.source_name}</span>
                  {article.published_at && (
                    <span className="font-mono tabular-nums text-ink-faint">{formatDate(article.published_at)}</span>
                  )}
                  <ImportanceBadge score={article.importance_score} />
                </div>
                <Link
                  to={`/article/${article.id}`}
                  className="block text-[15px] font-semibold leading-7 text-ink transition-colors group-hover:text-primary-600 dark:group-hover:text-primary-400"
                >
                  {article.title}
                </Link>
                {article.credit_relevance && (
                  <p className="mt-1.5 text-sm leading-6 text-accent-dark dark:text-accent">{article.credit_relevance}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Policy feed ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <div>
            <p className="text-label">Regulatory Feed</p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-ink">政策影响清单</h2>
          </div>
        </div>

        {isLoading && (
          <div className="paper-panel overflow-hidden divide-y divide-border">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2 px-5 py-4 animate-pulse">
                <div className="h-2.5 w-24 rounded bg-surface-sunken" />
                <div className="h-3.5 w-4/5 rounded bg-surface-sunken" />
                <div className="h-3 w-3/5 rounded bg-surface-sunken" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && articles.length === 0 && (
          <div className="paper-panel px-6 py-16 text-center">
            <p className="text-sm text-ink-secondary">当前筛选条件下暂无政策文章</p>
            <p className="mt-2 text-xs text-ink-tertiary">可放宽时间范围或调整影响等级筛选。</p>
          </div>
        )}

        {!isLoading && articles.length > 0 && (
          <div className="paper-panel overflow-hidden divide-y divide-border">
            {articles.map((article) => {
              const impact = article.policy_impact;
              const levelKey = impact?.impact_level ?? "";
              const dot = IMPACT_DOT[levelKey];
              const textCls = IMPACT_TEXT[levelKey];
              const baseType = impact?.policy_type?.replace(/_draft$/, "") ?? "";
              const isDraft = impact?.policy_type?.endsWith("_draft") ?? false;
              const typeLabel = TYPE_LABELS[baseType] ?? baseType;
              const affectedAreas = impact?.affected_areas ?? [];

              return (
                <div key={article.id} className="group px-5 py-4 transition-colors hover:bg-surface">
                  <div className="mb-2 flex flex-wrap items-center gap-2 text-2xs text-ink-tertiary">
                    {levelKey && (
                      <span className={`inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 font-medium ${textCls}`}>
                        {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
                        {IMPACT_LABELS[levelKey]}
                      </span>
                    )}
                    {typeLabel && (
                      <span className="rounded-full border border-border bg-surface px-2 py-0.5 font-medium text-ink-secondary">
                        {typeLabel}{isDraft ? " · 征求意见稿" : ""}
                      </span>
                    )}
                    <span>{article.source_name}</span>
                    {article.published_at && (
                      <span className="font-mono tabular-nums text-ink-faint">{formatDate(article.published_at)}</span>
                    )}
                    {impact?.effective_date && (
                      <span className="font-medium text-amber-700 dark:text-amber-400">生效 {impact.effective_date}</span>
                    )}
                    <ImportanceBadge score={article.importance_score} />
                  </div>

                  <Link
                    to={`/article/${article.id}`}
                    className="block text-[15px] font-semibold leading-7 text-ink transition-colors group-hover:text-primary-600 dark:group-hover:text-primary-400"
                  >
                    {article.title}
                  </Link>

                  {article.credit_relevance && (
                    <p className="mt-2 text-sm font-medium leading-6 text-accent-dark dark:text-accent">{article.credit_relevance}</p>
                  )}

                  {impact?.summary && (
                    <p className="mt-1.5 text-sm leading-6 text-ink-secondary">{impact.summary}</p>
                  )}

                  {article.risk_transmission && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-2xs font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                      风险传导 · {article.risk_transmission}
                    </div>
                  )}

                  {affectedAreas.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {affectedAreas.slice(0, 5).map((area) => (
                        <span
                          key={area}
                          className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-medium tracking-wide text-ink-tertiary"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
